import {
  autocompletion,
  type Completion,
  type CompletionContext,
  type CompletionResult,
} from '@codemirror/autocomplete';
import type { EditorView } from '@codemirror/view';
import type { Extension } from '@codemirror/state';
import { index } from '../worker/client.js';
import type { EditorCtx } from './context.js';

/**
 * [[wikilink]] autocomplete backed by the index worker: note names + aliases,
 * and `#heading` suggestions after a note name (or for the current note with
 * [[#...). Accepting inserts the link text and the closing ]].
 */

function applyLink(text: string) {
  return (view: EditorView, _completion: Completion, from: number, to: number) => {
    const closed = view.state.sliceDoc(to, to + 2) === ']]';
    const insert = closed ? text : `${text}]]`;
    const anchor = from + insert.length + (closed ? 2 : 0);
    view.dispatch({
      changes: { from, to, insert },
      selection: { anchor },
      userEvent: 'input.complete',
    });
  };
}

async function wikiSource(
  ctx: EditorCtx,
  context: CompletionContext,
): Promise<CompletionResult | null> {
  const m = context.matchBefore(/\[\[[^\[\]]*$/);
  if (!m) return null;
  const from = m.from + 2;
  const q = context.state.sliceDoc(from, context.pos);

  const hash = q.lastIndexOf('#');
  if (hash !== -1) {
    const notePart = q.slice(0, hash);
    const sub = q.slice(hash + 1).toLowerCase();
    let id: string | null = ctx.noteId;
    if (notePart !== '') id = ctx.resolve(notePart).id;
    if (!id) return null;
    const entry = await index.note(id);
    if (!entry || context.aborted) return null;
    const options: Completion[] = entry.headings
      .filter((h) => !sub || h.anchor.toLowerCase().includes(sub))
      .slice(0, 30)
      .map((h) => ({
        label: `# ${h.anchor}`,
        detail: `h${h.level}`,
        type: 'keyword',
        apply: applyLink(`${notePart}#${h.anchor}`),
      }));
    return options.length ? { from, options, filter: false } : null;
  }

  const suggestions = await index.suggestLinks(q, ctx.noteId, 24);
  if (context.aborted) return null;
  const options: Completion[] = suggestions.map((s) => ({
    label: s.display,
    detail: s.kind === 'alias' ? `alias — ${s.path}` : s.path,
    type: s.kind === 'heading' ? 'keyword' : 'text',
    apply: applyLink(s.linkText),
  }));
  return options.length ? { from, options, filter: false } : null;
}

export function wikilinkAutocomplete(ctx: EditorCtx): Extension {
  return autocompletion({
    override: [(context) => wikiSource(ctx, context)],
    icons: false,
    activateOnTypingDelay: 120,
    maxRenderedOptions: 30,
    defaultKeymap: true,
    closeOnBlur: true,
  });
}
